import * as z from 'zod';

export const GetLocaleSettingsRequest = z.object({});
export const GetLocaleSettingsResponse = z.object({
  localeSettings: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale settings are updated. To prevent conflicting changes, the existing revision must be passed when updating the locale settings.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale settings were last updated.')
        .optional()
        .nullable(),
      multilingualModeEnabled: z
        .boolean()
        .describe(
          'Whether multilingual mode is enabled for the site. When enabled, you can create secondary locales.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
      autoSwitch: z
        .boolean()
        .describe(
          'Whether the live site automatically opens in a secondary locale if the\nsite visitor\'s browser is set to that locale.\n\nDefault: `false`\n\n<blockquote class="caution">\n\n__Caution:__\nAuto-switch can harm SEO by causing duplicate content issues and restricting search engine indexing of different language versions, which limits visibility in search results.\n\n</blockquote>'
        )
        .optional()
        .nullable(),
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe(
          'Defines the default method for how secondary languages appear in the live site\'s URL.\nThe URL structure can be overridden per locale, using the Locale API.\n\nDefault: `"SUBDIRECTORY"`'
        )
        .optional(),
      primaryLocale: z
        .object({
          _id: z
            .string()
            .describe(
              'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
            )
            .max(20)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the locale was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the locale was last updated.')
            .optional()
            .nullable(),
          languageCode: z
            .string()
            .describe('Language code. For example, `"en"` for English.')
            .optional()
            .nullable(),
          regionCode: z
            .string()
            .describe('Region code. For example, `"UK"` for United Kingdom.')
            .optional()
            .nullable(),
          visibility: z
            .enum(['HIDDEN', 'VISIBLE'])
            .describe('Locale visibility status.')
            .optional(),
          primaryLocale: z
            .boolean()
            .describe("Whether the locale is the site's primary locale.")
            .optional()
            .nullable(),
          visitorPrimaryLocale: z
            .boolean()
            .describe(
              "Whether the locale is the site's visitor primary locale."
            )
            .optional()
            .nullable(),
          flag: z
            .string()
            .describe(
              'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
            )
            .max(3)
            .optional()
            .nullable(),
          regionalFormat: z
            .string()
            .describe(
              'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
            )
            .optional()
            .nullable(),
          effectiveUrlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .describe(
              "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
            )
            .optional(),
          urlStructureOverride: z
            .object({
              urlStructure: z
                .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                .describe('URL structure to use instead of the default.')
                .optional(),
            })
            .describe(
              "Optional override for the locale's default URL structure."
            )
            .optional(),
          machineTranslationCode: z
            .string()
            .describe(
              'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
            )
            .optional()
            .nullable(),
          effectiveDisplayName: z
            .string()
            .describe("The locale's `displayName`, unless overridden.")
            .max(30)
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Default display name for the locale, inferred from the locale ID and region code.'
            )
            .max(30)
            .optional()
            .nullable(),
          overrideDisplayName: z
            .string()
            .describe("Optional override for the locale's display name.")
            .max(30)
            .optional()
            .nullable(),
        })
        .describe("The site's primary locale.")
        .optional(),
    })
    .describe('Retrieved locale settings.')
    .optional(),
});
export const UpdateLocaleSettingsRequest = z.object({
  localeSettings: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale settings are updated. To prevent conflicting changes, the existing revision must be passed when updating the locale settings.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the locale settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale settings were last updated.')
        .optional()
        .nullable(),
      multilingualModeEnabled: z
        .boolean()
        .describe(
          'Whether multilingual mode is enabled for the site. When enabled, you can create secondary locales.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
      autoSwitch: z
        .boolean()
        .describe(
          'Whether the live site automatically opens in a secondary locale if the\nsite visitor\'s browser is set to that locale.\n\nDefault: `false`\n\n<blockquote class="caution">\n\n__Caution:__\nAuto-switch can harm SEO by causing duplicate content issues and restricting search engine indexing of different language versions, which limits visibility in search results.\n\n</blockquote>'
        )
        .optional()
        .nullable(),
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .optional(),
      primaryLocale: z
        .object({
          _id: z
            .string()
            .describe(
              'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
            )
            .max(20)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the locale was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the locale was last updated.')
            .optional()
            .nullable(),
          languageCode: z
            .string()
            .describe('Language code. For example, `"en"` for English.')
            .optional()
            .nullable(),
          regionCode: z
            .string()
            .describe('Region code. For example, `"UK"` for United Kingdom.')
            .optional()
            .nullable(),
          visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
          primaryLocale: z
            .boolean()
            .describe("Whether the locale is the site's primary locale.")
            .optional()
            .nullable(),
          visitorPrimaryLocale: z
            .boolean()
            .describe(
              "Whether the locale is the site's visitor primary locale."
            )
            .optional()
            .nullable(),
          flag: z
            .string()
            .describe(
              'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
            )
            .max(3)
            .optional()
            .nullable(),
          regionalFormat: z
            .string()
            .describe(
              'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
            )
            .optional()
            .nullable(),
          effectiveUrlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .optional(),
          urlStructureOverride: z
            .object({
              urlStructure: z
                .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                .optional(),
            })
            .describe(
              "Optional override for the locale's default URL structure."
            )
            .optional(),
          machineTranslationCode: z
            .string()
            .describe(
              'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
            )
            .optional()
            .nullable(),
          effectiveDisplayName: z
            .string()
            .describe("The locale's `displayName`, unless overridden.")
            .max(30)
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Default display name for the locale, inferred from the locale ID and region code.'
            )
            .max(30)
            .optional()
            .nullable(),
          overrideDisplayName: z
            .string()
            .describe("Optional override for the locale's display name.")
            .max(30)
            .optional()
            .nullable(),
        })
        .describe("The site's primary locale.")
        .optional(),
    })
    .describe('Locale settings to update.'),
});
export const UpdateLocaleSettingsResponse = z.object({
  localeSettings: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale settings are updated. To prevent conflicting changes, the existing revision must be passed when updating the locale settings.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale settings were last updated.')
        .optional()
        .nullable(),
      multilingualModeEnabled: z
        .boolean()
        .describe(
          'Whether multilingual mode is enabled for the site. When enabled, you can create secondary locales.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
      autoSwitch: z
        .boolean()
        .describe(
          'Whether the live site automatically opens in a secondary locale if the\nsite visitor\'s browser is set to that locale.\n\nDefault: `false`\n\n<blockquote class="caution">\n\n__Caution:__\nAuto-switch can harm SEO by causing duplicate content issues and restricting search engine indexing of different language versions, which limits visibility in search results.\n\n</blockquote>'
        )
        .optional()
        .nullable(),
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe(
          'Defines the default method for how secondary languages appear in the live site\'s URL.\nThe URL structure can be overridden per locale, using the Locale API.\n\nDefault: `"SUBDIRECTORY"`'
        )
        .optional(),
      primaryLocale: z
        .object({
          _id: z
            .string()
            .describe(
              'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
            )
            .max(20)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the locale was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the locale was last updated.')
            .optional()
            .nullable(),
          languageCode: z
            .string()
            .describe('Language code. For example, `"en"` for English.')
            .optional()
            .nullable(),
          regionCode: z
            .string()
            .describe('Region code. For example, `"UK"` for United Kingdom.')
            .optional()
            .nullable(),
          visibility: z
            .enum(['HIDDEN', 'VISIBLE'])
            .describe('Locale visibility status.')
            .optional(),
          primaryLocale: z
            .boolean()
            .describe("Whether the locale is the site's primary locale.")
            .optional()
            .nullable(),
          visitorPrimaryLocale: z
            .boolean()
            .describe(
              "Whether the locale is the site's visitor primary locale."
            )
            .optional()
            .nullable(),
          flag: z
            .string()
            .describe(
              'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
            )
            .max(3)
            .optional()
            .nullable(),
          regionalFormat: z
            .string()
            .describe(
              'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
            )
            .optional()
            .nullable(),
          effectiveUrlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .describe(
              "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
            )
            .optional(),
          urlStructureOverride: z
            .object({
              urlStructure: z
                .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                .describe('URL structure to use instead of the default.')
                .optional(),
            })
            .describe(
              "Optional override for the locale's default URL structure."
            )
            .optional(),
          machineTranslationCode: z
            .string()
            .describe(
              'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
            )
            .optional()
            .nullable(),
          effectiveDisplayName: z
            .string()
            .describe("The locale's `displayName`, unless overridden.")
            .max(30)
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Default display name for the locale, inferred from the locale ID and region code.'
            )
            .max(30)
            .optional()
            .nullable(),
          overrideDisplayName: z
            .string()
            .describe("Optional override for the locale's display name.")
            .max(30)
            .optional()
            .nullable(),
        })
        .describe("The site's primary locale.")
        .optional(),
    })
    .describe('Updated locale settings.')
    .optional(),
});
export const SetMultilingualModeRequest = z.object({
  multilingualModeEnabled: z
    .boolean()
    .describe(
      'Whether to enable multilingual mode for the site.\n\nDefault: `false`'
    ),
});
export const SetMultilingualModeResponse = z.object({
  localeSettings: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale settings are updated. To prevent conflicting changes, the existing revision must be passed when updating the locale settings.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale settings were last updated.')
        .optional()
        .nullable(),
      multilingualModeEnabled: z
        .boolean()
        .describe(
          'Whether multilingual mode is enabled for the site. When enabled, you can create secondary locales.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
      autoSwitch: z
        .boolean()
        .describe(
          'Whether the live site automatically opens in a secondary locale if the\nsite visitor\'s browser is set to that locale.\n\nDefault: `false`\n\n<blockquote class="caution">\n\n__Caution:__\nAuto-switch can harm SEO by causing duplicate content issues and restricting search engine indexing of different language versions, which limits visibility in search results.\n\n</blockquote>'
        )
        .optional()
        .nullable(),
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe(
          'Defines the default method for how secondary languages appear in the live site\'s URL.\nThe URL structure can be overridden per locale, using the Locale API.\n\nDefault: `"SUBDIRECTORY"`'
        )
        .optional(),
      primaryLocale: z
        .object({
          _id: z
            .string()
            .describe(
              'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
            )
            .max(20)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the locale was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the locale was last updated.')
            .optional()
            .nullable(),
          languageCode: z
            .string()
            .describe('Language code. For example, `"en"` for English.')
            .optional()
            .nullable(),
          regionCode: z
            .string()
            .describe('Region code. For example, `"UK"` for United Kingdom.')
            .optional()
            .nullable(),
          visibility: z
            .enum(['HIDDEN', 'VISIBLE'])
            .describe('Locale visibility status.')
            .optional(),
          primaryLocale: z
            .boolean()
            .describe("Whether the locale is the site's primary locale.")
            .optional()
            .nullable(),
          visitorPrimaryLocale: z
            .boolean()
            .describe(
              "Whether the locale is the site's visitor primary locale."
            )
            .optional()
            .nullable(),
          flag: z
            .string()
            .describe(
              'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
            )
            .max(3)
            .optional()
            .nullable(),
          regionalFormat: z
            .string()
            .describe(
              'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
            )
            .optional()
            .nullable(),
          effectiveUrlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .describe(
              "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
            )
            .optional(),
          urlStructureOverride: z
            .object({
              urlStructure: z
                .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                .describe('URL structure to use instead of the default.')
                .optional(),
            })
            .describe(
              "Optional override for the locale's default URL structure."
            )
            .optional(),
          machineTranslationCode: z
            .string()
            .describe(
              'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
            )
            .optional()
            .nullable(),
          effectiveDisplayName: z
            .string()
            .describe("The locale's `displayName`, unless overridden.")
            .max(30)
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Default display name for the locale, inferred from the locale ID and region code.'
            )
            .max(30)
            .optional()
            .nullable(),
          overrideDisplayName: z
            .string()
            .describe("Optional override for the locale's display name.")
            .max(30)
            .optional()
            .nullable(),
        })
        .describe("The site's primary locale.")
        .optional(),
    })
    .describe('Updated locale settings.')
    .optional(),
});
